import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';

import routes from '../../../shared/routes';
import httpClient from '../../../shared/libraries/httpClient';

import MessageCard from './MessageCard';
import MessageTyper from './MessageTyper';
import DraftMessage from './DraftMessage';

const MessageThread = props => {
  const [messages, setMessages] = useState([]);
  const [messageToEdit, setMessageToEdit] = useState(null);
  const [messageToEditId, setMessageToEditId] = useState(null);
  const [uncommittedMessage, setUncommittedMessage] = useState('');
  const [uncommittedMessageId, setUncommittedMessageId] = useState('');
  const {
    selectedSupportTicketId,
    ticketCreatedBy,
    fetchTickets,
    signedInAsSuperAdmin,
  } = props;
  const messageThreadRef = useRef(null);
  const previousDraftMessageCount = useRef(0);
  const previousInternalMessageCount = useRef(0);

  const fetchMessages = async ticketId => {
    const { data } = await httpClient.get(
      routes.support.ticketMessages({
        support_ticket_id: ticketId || selectedSupportTicketId,
      }),
    );

    const threadMessages = data.messages.filter(
      message => message.state !== 'uncommitted',
    );

    setMessages(threadMessages);
    setUncommittedMessageId(data?.uncommittedMessage?.id);

    return data?.uncommittedMessage?.body;
  };

  const draftMessage = messages.filter(message => message.state === 'draft');
  const internalMessage = messages.filter(message => message.internal);
  const hasDraftMessage = draftMessage.length > 0;
  const showDraft = () =>
    hasDraftMessage && !messageToEdit && signedInAsSuperAdmin;

  const scrollToBottomOfMessageThread = () => {
    const scrollToBottom =
      messageThreadRef.current.scrollHeight -
      messageThreadRef.current.clientHeight;
    messageThreadRef.current.scrollTo(0, scrollToBottom);
  };

  useEffect(() => {
    if (
      typeof selectedSupportTicketId !== 'undefined' &&
      selectedSupportTicketId !== null
    ) {
      fetchMessages()
        .then(message => {
          setUncommittedMessage(message || '');
        })
        .catch(() => {
          setUncommittedMessage('');
        });
    }
  }, [selectedSupportTicketId]);

  useEffect(() => {
    const previousAndNextDraftMessageCountEquals =
      previousDraftMessageCount.current === draftMessage.length;
    const previousAndNextInternalMessageCountEquals =
      previousInternalMessageCount.current === internalMessage.length;

    if (
      messageThreadRef.current &&
      (!previousAndNextDraftMessageCountEquals ||
        !previousAndNextInternalMessageCountEquals)
    ) {
      previousDraftMessageCount.current = draftMessage.length;
      previousInternalMessageCount.current = internalMessage.length;
      scrollToBottomOfMessageThread();
    }
  }, [messages]);

  return (
    <div className="support-message-thread">
      <div ref={messageThreadRef} className="support-message-thread__messages">
        {messages
          .filter(message => message.state !== 'draft')
          .map(message => (
            <MessageCard
              signedInAsSuperAdmin={signedInAsSuperAdmin}
              fetchMessages={fetchMessages}
              selectedSupportTicketId={selectedSupportTicketId}
              ticketCreatedBy={ticketCreatedBy}
              message={message}
              key={message.id}
              setMessageToEdit={setMessageToEdit}
              setMessageToEditId={setMessageToEditId}
            />
          ))}
      </div>
      {showDraft() ? (
        <DraftMessage
          message={draftMessage[0]}
          fetchTickets={fetchTickets}
          fetchMessages={fetchMessages}
          selectedSupportTicketId={selectedSupportTicketId}
          setMessageToEdit={setMessageToEdit}
          setMessageToEditId={setMessageToEditId}
          setUncommittedMessage={setUncommittedMessage}
        />
      ) : (
        selectedSupportTicketId && (
          <div className="support-message-thread__editor">
            <MessageTyper
              uncommittedMessage={messageToEdit || uncommittedMessage}
              uncommittedMessageId={messageToEditId || uncommittedMessageId}
              editing={!!messageToEdit}
              selectedSupportTicketId={selectedSupportTicketId}
              fetchTickets={fetchTickets}
              fetchMessages={fetchMessages}
              signedInAsSuperAdmin={signedInAsSuperAdmin}
              setMessageToEdit={setMessageToEdit}
              setMessageToEditId={setMessageToEditId}
            />
          </div>
        )
      )}
    </div>
  );
};

MessageThread.defaultProps = {
  selectedSupportTicketId: null,
  ticketCreatedBy: null,
};

MessageThread.propTypes = {
  selectedSupportTicketId: PropTypes.number,
  ticketCreatedBy: PropTypes.shape({
    name: PropTypes.string,
    superAdmin: PropTypes.bool,
  }),
  fetchTickets: PropTypes.func.isRequired,
  signedInAsSuperAdmin: PropTypes.bool.isRequired,
};

export default MessageThread;
